import { ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import DownloadIcon from '@mui/icons-material/Download';
import { CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import type { SxProps, Theme } from '@mui/material';
import { useCanAccess, useNotify } from 'react-admin';
import { useReportClickSummaryCreate } from '../../../../DataProvider';
import type { ReportFilter } from '../../../../Components/ReportsWidget/utils';
import { getClicksSummaryReportParams, downloadClicksSummariesCsv } from './utils';

export interface ExportButtonProps {
  sx?: SxProps<Theme>;
}

export default function ExportButton({ sx }: ExportButtonProps): ReactElement | null {
  const { handleSubmit } = useFormContext();
  const notify = useNotify();
  const [runReport, report] = useReportClickSummaryCreate();

  const reportsCreateAccess = useCanAccess({ resource: 'reports', action: 'create' });
  const clicksSummariesListAccess = useCanAccess({
    resource: 'clicks_summaries',
    action: 'list',
  });

  const handleExport = handleSubmit(async (values) => {
    if (report.isPending) {
      return;
    }

    try {
      const filter = values as ReportFilter;
      const result = await runReport(getClicksSummaryReportParams(filter));
      downloadClicksSummariesCsv(result.data, filter);
      notify('Export downloaded', { type: 'success' });
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Export failed.', { type: 'error' });
    }
  });

  if (!reportsCreateAccess.canAccess || !clicksSummariesListAccess.canAccess) {
    return null;
  }

  return (
    <Button
      variant="contained"
      onClick={() => void handleExport()}
      disabled={report.isPending}
      sx={sx}
      startIcon={report.isPending ? <CircularProgress size={16} /> : <DownloadIcon />}
    >
      {report.isPending ? 'Exporting...' : 'Export CSV'}
    </Button>
  );
}
